"use client"

import {zodResolver} from "@hookform/resolvers/zod"
import {useForm} from "react-hook-form"
import {useState} from "react"
import {Eye, EyeOff} from "lucide-react"
import axiosInstance from "@/api/axios"
import {useToast} from "@/hooks/use-toast"

import type {ChangePasswordFormType} from "../../types"

import {ChangePasswordSchema} from "../schemas/chnage-password-schema"

import {Button} from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import {Input} from "@/components/ui/input"

export function ChangePasswordForm() {
    const {toast} = useToast()
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const form = useForm<ChangePasswordFormType>({
        resolver: zodResolver(ChangePasswordSchema),
        defaultValues: {
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        },
    })

    const {isSubmitting} = form.formState

    async function onSubmit(data: ChangePasswordFormType) {
        try {
            const csrfResponse = await axiosInstance.get("csrf/");
            const csrfToken = csrfResponse.data.csrfToken;

            const response = await axiosInstance.post('user/change_password', {
                current_password: data.currentPassword,
                new_password: data.newPassword,
                confirm_password: data.confirmPassword,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": csrfToken,
                },
                withCredentials: true,
            })

            if (response.data.status === 'success') {
                form.reset()
                toast({
                    title: "Success",
                    description: "Password changed successfully! Check your email. Logging you out...",
                })

                if (response.data.logout) {
                    setTimeout(async () => {
                        await axiosInstance.post('user/logout')
                        window.location.href = '/login'
                    }, 2000)
                }
            }
        } catch (error: any) {
            if (error.response?.data) {
                const errors = error.response.data

                if (errors.current_password) {
                    form.setError('currentPassword', {
                        message: Array.isArray(errors.current_password)
                            ? errors.current_password[0]
                            : errors.current_password
                    })
                }
                if (errors.new_password) {
                    form.setError('newPassword', {
                        message: Array.isArray(errors.new_password)
                            ? errors.new_password[0]
                            : errors.new_password
                    })
                }
                if (errors.confirm_password) {
                    form.setError('confirmPassword', {
                        message: Array.isArray(errors.confirm_password)
                            ? errors.confirm_password[0]
                            : errors.confirm_password
                    })
                }

                if (!errors.current_password && !errors.new_password && !errors.confirm_password) {
                    toast({
                        title: "Error",
                        description: "Failed to change password. Please try again.",
                        variant: "destructive",
                    })
                }
            } else {
                toast({
                    title: "Error",
                    description: "An unexpected error occurred.",
                    variant: "destructive",
                })
            }
            console.error('Error changing password:', error);
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-y-2">
                <FormField
                    control={form.control}
                    name="currentPassword"
                    render={({field}) => (
                        <FormItem>
                            <FormLabel>Current Password</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        type={showCurrentPassword ? "text" : "password"}
                                        {...field}
                                        className="pr-10"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    >
                                        {showCurrentPassword ? (
                                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <span className="sr-only">
                                            {showCurrentPassword ? "Hide password" : "Show password"}
                                        </span>
                                    </Button>
                                </div>
                            </FormControl>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="newPassword"
                    render={({field}) => (
                        <FormItem>
                            <FormLabel>New Password</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        type={showNewPassword ? "text" : "password"}
                                        {...field}
                                        className="pr-10"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                        {showNewPassword ? (
                                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <span className="sr-only">
                                            {showNewPassword ? "Hide password" : "Show password"}
                                        </span>
                                    </Button>
                                </div>
                            </FormControl>
                            <FormMessage/>
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({field}) => (
                        <FormItem>
                            <FormLabel>Confirm Password</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        type={showConfirmPassword ? "text" : "password"}
                                        {...field}
                                        className="pr-10"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                        {showConfirmPassword ? (
                                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <span className="sr-only">
                                            {showConfirmPassword ? "Hide password" : "Show password"}
                                        </span>
                                    </Button>
                                </div>
                            </FormControl>
                            <FormMessage/>
                        </FormItem>
                    )}
                />

                <Button className="mt-2 w-fit" disabled={isSubmitting}>
                    {isSubmitting ? "Changing..." : "Set new password"}
                </Button>
            </form>
        </Form>
    )
}
